/**
 * Vendor Apps store — single source of truth for the host side of
 * docs/vendor-apps-contract.md. Vanilla pub/sub (same Emitter as liveStore)
 * so the bridge, the evaluator, and React can all read it.
 *
 * State it owns:
 *   apps            registered apps for this group (C1 later; localStorage override now)
 *   patient         the open chart: { id, name, dob } | null, plus conversationId
 *   contextVersion  monotonic; bumps when transcript/note content changes
 *   buffers         (appId, patientId) → { markdown, patientRef, patientName, providedAt }
 *   providerBuffers appId → { markdown, providedAt }   about the clinician, not a patient; saved on the user record for chat
 *   subs            appId → subId → { structure, compiled, types, hash, lastKey }
 *   status          appId → { linkedAs, lastProvideAt, lastError, lastEvaluatedAt }
 *   refs            (appId, patientId) → patientRef   (C3 later; localStorage now)
 */
import { Emitter } from '../../agent/events';
import { namesResemble } from './namesResemble';
import { compileStructure, hashStructure } from './flexCompile';

// Character budget, not bytes or tokens; mirrors comprehend.js.
export const MAX_CONTEXT_CHARS = 16 * 1024;
export const MAX_SUBSCRIPTIONS = 10;

export const APPS_OVERRIDE_KEY = 'comprehendpt_vendor_apps_override';
const REFS_KEY = 'comprehendpt_vendor_refs';
const DEV_ENABLED_KEY = 'comprehendpt_vendor_apps_dev';
const USED_KEY = 'comprehendpt_vendor_apps_used'; // { appId: lastOpenedIso } — apps this provider has opened at least once on this browser
const ACTIVE_KEY = 'comprehendpt_vendor_active'; // sessionStorage: { conversationId: appId }

const readJSON = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return v && typeof v === 'object' ? v : fallback;
  } catch (e) {
    return fallback;
  }
};
const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* private mode etc. — refs just won't survive reload */
  }
};

/**
 * DEMO: EMRs don't give us patient contact details yet (getPatientInfo returns
 * name/DOB only), so every patient carries this email and mobile until they do.
 * A real `email` / `phone` on the patient always wins. Set to null to turn off.
 */
export const DEMO_PATIENT_CONTACT = { email: 'patient@example.com', phone: '5555550123' };

/** apiClient.patients entry → contract patient (+ server-side vendorRefs, merged into the store on setPatient). */
export const patientFromRecord = (record) => {
  if (!record) return null;
  const name = [record.firstName, record.lastName].filter(Boolean).join(' ').trim();
  return {
    id: String(record.uid), name, dob: record.dob || null,
    email: record.email || null, phone: record.phone || record.mobile || null,
    vendorRefs: record.vendorRefs || null,
  };
};

/**
 * Normalise a registered app; `embedOrigin` is the identity, so derive it from
 * launchUrl if absent. `kind` is 'iframe' (the contract: a vendor page in the
 * Apps tab) or 'adapter' (a Comprehend-built connector that reads the vendor's
 * own site in the clinician's other tab and provides on their behalf — data
 * model only for now; the host never frames an adapter).
 */
export const normalizeApp = (app) => {
  if (!app) return null;
  const kind = app.kind === 'adapter' ? 'adapter' : 'iframe';
  let embedOrigin = app.embedOrigin;
  if (kind === 'iframe') {
    if (!app.launchUrl) return null;
    try {
      if (!embedOrigin) embedOrigin = new URL(app.launchUrl).origin;
    } catch (e) {
      return null;
    }
  } else if (!embedOrigin) {
    return null;
  }
  return {
    id: String(app.id || embedOrigin),
    kind,
    name: app.name || embedOrigin,
    embedOrigin,
    launchUrl: kind === 'iframe' ? app.launchUrl : null,
    enabled: app.enabled !== false,
    baa: app.baa || null,
    // Presentation (from the vendor's manifest at registration; all optional).
    description: app.description || '',
    icon: app.icon || '',
    color: /^#[0-9a-f]{6}$/i.test(app.color || '') ? app.color : '',
  };
};

export class VendorStore {
  constructor() {
    this.emitter = new Emitter();
    this.apps = [];
    this.patient = null;
    this.conversationId = null;
    this.contextVersion = 0;
    this.buffers = new Map();
    this.providerBuffers = new Map();
    this.pending = new Map(); // appId → rejected setContext the clinician may confirm
    this.pendingAssign = new Map(); // appId → setContext sent while the visit had NO patient; the clinician may pull that patient in
    this.subs = new Map();
    this.status = new Map();
    this.refs = readJSON(REFS_KEY, {});
    this.activeAppId = null;
    this.persistRef = null; // (patientId, appId, patientRef) => Promise — wired to the backend in C3
    this.persistProviderContext = null; // (appId, markdown) => Promise — the user record; '' clears
    // Dev Console switches these; production never touches them.
    this.evalMode = 'live'; // 'live' | 'mock'
    this.mockScenario = 'none'; // see evaluator MOCK_SCENARIOS
  }

  /** Dev Console: mount the host locally without the backend `vendor_apps` flag. */
  get devEnabled() {
    try {
      return localStorage.getItem(DEV_ENABLED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  setDevEnabled(on) {
    try {
      if (on) localStorage.setItem(DEV_ENABLED_KEY, '1');
      else localStorage.removeItem(DEV_ENABLED_KEY);
    } catch (e) {
      /* ignore */
    }
    this._changed('devMode', { on: Boolean(on) });
  }

  setEvalMode(mode, scenario = this.mockScenario) {
    this.evalMode = mode;
    this.mockScenario = scenario;
    this._changed('evalMode', { mode, scenario });
  }

  /**
   * Clinician-action trigger (contract §3): note generate (Comprehend) or Refresh
   * apps. The host listens and runs the evaluator for every mounted app.
   */
  // Reasons: 'note' (the clinician pressed Comprehend), 'refresh' (Apps tab
  // Refresh), 'manual' (an app's own refresh). Nothing else evaluates — not
  // subscribing, not opening a chart.
  requestEvaluate(reason = 'refresh', appId = null) {
    this._changed('evaluate', { reason, appId });
  }
  /** The clinician changed an app's zoom (apps that report comprehend:zoom). */
  requestZoom(appId, zoom) {
    this._changed('zoom', { appId, zoom });
  }

  // ---- subscriptions to the store itself -------------------------------
  on(event, cb) {
    return this.emitter.on(event, cb);
  }
  _changed(what, payload) {
    this.emitter.emit('change', { what, ...payload });
    this.emitter.emit(what, payload);
  }

  // ---- apps -------------------------------------------------------------
  /**
   * Make sure the registered apps are loaded. The profile payload
   * (`apiClient.vendorApps`, from /v1/user/stats) is used when it has already
   * arrived; otherwise — or when it is stale — ask the backend directly. Safe to
   * call from every surface that shows apps; de-duplicates in-flight fetches and
   * refreshes at most every 30 s.
   */
  ensureApps(apiClient, { force = false, maxAgeMs = 15000 } = {}) {
    if (!apiClient) return Promise.resolve(this.apps);
    const now = Date.now();
    if (!force && this._appsLoadedAt && now - this._appsLoadedAt < maxAgeMs) return Promise.resolve(this.apps);
    if (this._appsInflight) return this._appsInflight;
    // First paint from the profile's list if we have nothing yet; the backend is
    // still asked, so an app an admin just added shows up without a reload.
    if (!this.apps.length && Array.isArray(apiClient.vendorApps) && apiClient.vendorApps.length) this.loadApps(apiClient.vendorApps);
    if (typeof apiClient.listVendorApps !== 'function') return Promise.resolve(this.apps);
    // Not until the profile is in: the dev/prod backend switch happens when /v1/user/stats
    // returns, so a call before that goes to the wrong backend (and its failure would be
    // cached as "no apps"). And when the profile says this backend has no Vendor Apps
    // routes (flag on, backend not deployed — e.g. prod before release): nothing to list.
    if (apiClient.vendorAppsSupported !== true) return Promise.resolve(this.apps);
    this._appsInflight = Promise.resolve(apiClient.listVendorApps())
      .then((rsp) => {
        const list = Array.isArray(rsp?.vendor_apps) ? rsp.vendor_apps.filter((a) => a.enabled) : [];
        apiClient.vendorApps = list;
        this.loadApps(list);
        this._appsLoadedAt = Date.now();
        return this.apps;
      })
      .catch(() => this.apps)
      .finally(() => {
        this._appsInflight = null;
      });
    return this._appsInflight;
  }
  setApps(apps) {
    this.apps = (apps || []).map(normalizeApp).filter(Boolean);
    // Forget "used" apps that were turned off or removed — but only against a real
    // list. The store sees an empty list on boot before the profile/app list has
    // arrived; pruning then would wipe the set on every page load.
    if (this.apps.length) {
      const used = readJSON(USED_KEY, {});
      const pruned = Object.fromEntries(Object.entries(used).filter(([id]) => this.apps.find((a) => a.id === id && a.enabled)));
      if (Object.keys(pruned).length !== Object.keys(used).length) writeJSON(USED_KEY, pruned);
    }
    this._changed('apps', { apps: this.apps });
  }
  /** Registered apps, straight from the backend list. */
  loadApps(registered) {
    // An early dev build let a localStorage override stand in for the registered
    // list. Nothing writes it any more, but a browser that still carries one would
    // silently shadow the real apps — so clear it rather than honour it.
    try {
      localStorage.removeItem(APPS_OVERRIDE_KEY);
    } catch (e) {
      /* ignore */
    }
    this.setApps(registered || []);
  }
  getApp(appId) {
    return this.apps.find((a) => a.id === appId) || null;
  }
  setActiveApp(appId) {
    this.activeAppId = appId;
    if (appId && this.conversationId != null) {
      try {
        const m = JSON.parse(sessionStorage.getItem(ACTIVE_KEY) || '{}');
        m[String(this.conversationId)] = appId;
        sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(m));
      } catch (e) {
        /* ignore */
      }
    }
    this._changed('activeApp', { appId });
  }
  /** The app that was showing for this visit earlier in this tab (survives a refresh), or null. */
  recallActiveApp(conversationId = this.conversationId) {
    try {
      const id = JSON.parse(sessionStorage.getItem(ACTIVE_KEY) || '{}')[String(conversationId)];
      return id && this.getApp(id)?.enabled ? id : null;
    } catch (e) {
      return null;
    }
  }

  // ---- "used" apps: opened at least once by this provider on this browser --------
  // These are the apps worth loading in the background when a visit opens: they
  // have been signed into here, so they boot silently, get the patient, set
  // context and subscribe before the clinician ever opens the Apps tab. Apps never
  // opened stay lazy — a login page nobody has used gains nothing loading hidden.
  markUsed(appId) {
    if (!appId) return;
    const used = readJSON(USED_KEY, {});
    used[appId] = new Date().toISOString();
    writeJSON(USED_KEY, used);
  }
  /** Enabled apps this provider has opened before, most recent first. */
  usedAppIds() {
    const used = readJSON(USED_KEY, {});
    return Object.entries(used)
      .filter(([id]) => this.getApp(id)?.enabled)
      .sort((a, b) => String(b[1]).localeCompare(String(a[1])))
      .map(([id]) => id);
  }

  // ---- open chart ---------------------------------------------------------
  /** Called by the conversation view whenever the chart (patient) or conversation changes. */
  setPatient(patient, conversationId) {
    const prevIdentity = JSON.stringify(this.patient);
    const prevRefs = JSON.stringify(this.refs);
    const prevConvo = this.conversationId;
    const realId = (c) => c != null && c !== -1 && String(c) !== '-1';
    if (realId(prevConvo) && realId(conversationId) && String(prevConvo) !== String(conversationId)) {
      // A different visit: what apps proposed for the last one no longer applies.
      this.pendingAssign.forEach((_, appId) => this._setStatus(appId, { awaitingPatient: null }));
      this.pendingAssign.clear();
      this.pending.clear();
    }
    this.patient = patient ? {
      id: String(patient.id), name: patient.name || '', dob: patient.dob || null,
      // Optional contact details; apps that send things to the patient use them.
      email: patient.email || DEMO_PATIENT_CONTACT?.email || null,
      phone: patient.phone || DEMO_PATIENT_CONTACT?.phone || null,
    } : null;
    this.conversationId = conversationId ?? null;
    // Server-side links (C3) win over whatever this browser remembered.
    if (patient?.vendorRefs && typeof patient.vendorRefs === 'object') {
      for (const [appId, ref] of Object.entries(patient.vendorRefs)) {
        if (ref != null) this.refs[`${appId}:${String(patient.id)}`] = String(ref);
      }
      writeJSON(REFS_KEY, this.refs);
    }
    if (JSON.stringify(this.patient) !== prevIdentity || JSON.stringify(this.refs) !== prevRefs || String(this.conversationId) !== String(prevConvo)) {
      this.contextVersion = 0;
      this._changed('patient', { patient: this.patient, conversationId: this.conversationId });
      if (this.patient) {
        // A patient landed on the visit. Only the app whose patient the clinician
        // chose has its held context replayed (through the normal guard). Every
        // other app simply gets patient(changed) and re-syncs to the new patient —
        // replaying theirs would raise a mismatch for a proposal nobody picked.
        const source = this._assignSource;
        this._assignSource = null;
        const held = Array.from(this.pendingAssign.entries());
        this.pendingAssign.clear();
        held.forEach(([appId, p]) => {
          this._setStatus(appId, { awaitingPatient: null });
          if (appId === source) this.setContext(appId, { markdown: p.markdown, patientId: this.patient.id, name: p.name, yourId: p.yourId, dob: p.dob });
        });
      }
    }
  }
  bumpContext() {
    this.contextVersion += 1;
    this._changed('context', { contextVersion: this.contextVersion });
    return this.contextVersion;
  }
  /** The patient object the vendor sees — includes THEIR ref for this patient. */
  patientFor(appId) {
    if (!this.patient) return null;
    return { ...this.patient, ref: this.getRef(appId, this.patient.id) };
  }

  // ---- links -------------------------------------------------------------
  getRef(appId, patientId) {
    return this.refs[`${appId}:${patientId}`] ?? null;
  }
  setRef(appId, patientId, patientRef) {
    if (patientRef == null) return;
    const key = `${appId}:${patientId}`;
    if (this.refs[key] === patientRef) return;
    this.refs[key] = patientRef;
    writeJSON(REFS_KEY, this.refs);
    if (this.persistRef) {
      Promise.resolve(this.persistRef(patientId, appId, patientRef)).catch((e) =>
        console.warn('[vendorApps] persistRef failed', e),
      );
    }
  }

  // ---- context -----------------------------------------------------------
  /**
   * Host-side acceptance of a vendor's setContext. `patientId` is ours (from
   * the handle they called it on); `name`/`yourId` are THEIR patient. Returns
   * { ok: true } or { ok: false, code, reason, message }. Content AND link are
   * rejected together — the markdown is probably the wrong patient's.
   */
  setContext(appId, { markdown, patientId, name, yourId, dob = null }) {
    if (typeof markdown === 'string' && markdown.length > MAX_CONTEXT_CHARS) {
      return { ok: false, code: 'CONTEXT_TOO_LARGE', message: `context exceeds ${MAX_CONTEXT_CHARS} characters; send a concise summary` };
    }
    const open = this.patient;
    if (!open) {
      // The visit has no patient yet. Hold what the app sent; the clinician can
      // pull that patient onto the visit from Comprehend's UI (assignFromApp).
      // Nothing is assigned automatically.
      this.pendingAssign.set(appId, { markdown, name, dob: dob || null, yourId: yourId ?? null });
      this._setStatus(appId, { awaitingPatient: { name, dob: dob || null } });
      this._changed('proposeAssign', { appId, name, dob: dob || null, yourId: yourId ?? null });
      // Not an error for the app: nothing is stale, the clinician just hasn't decided. The bridge sends nothing back.
      return { ok: false, code: 'HELD_NO_PATIENT', silent: true, message: 'no patient on the visit — held for the clinician to pull in' };
    }
    if (patientId != null && String(patientId) !== open.id) {
      return {
        ok: false,
        code: 'PATIENT_MISMATCH',
        reason: 'NOT_OPEN',
        message: `context was for patient ${patientId}; open chart is ${open.id}`,
      };
    }
    // Who decides the two records are the same person, in order:
    //  1. a link that already exists for this app + patient (made by a past accepted
    //     setContext, or confirmed by the clinician) — their id is authoritative;
    //  2. the same date of birth on both sides, when both sides have one;
    //  3. the names resemble each other (namesResemble);
    //  4. otherwise the clinician, via confirmLink() from Comprehend's UI.
    const ref = this.getRef(appId, open.id);
    const linkedBefore = (yourId != null && ref === String(yourId)) || (ref != null && ref === `name:${name}`);
    const sameDob = Boolean(dob && open.dob && String(dob).slice(0, 10) === String(open.dob).slice(0, 10));
    if (!linkedBefore && !sameDob && !namesResemble(open.name, name)) {
      this.pending.set(appId, { markdown, patientId: open.id, name, yourId: yourId ?? null });
      this._setStatus(appId, { lastError: { code: 'PATIENT_MISMATCH', reason: 'NAME_MISMATCH', theirs: name, ours: open.name } });
      this._changed('mismatch', { appId, theirs: name, ours: open.name });
      return {
        ok: false,
        code: 'PATIENT_MISMATCH',
        reason: 'NAME_MISMATCH',
        message: `"${name}" does not resemble the open chart "${open.name}"`,
      };
    }
    this.pending.delete(appId);
    return this._acceptContext(appId, open, { markdown, name, yourId });
  }

  /** What an app sent while the visit had no patient (or null). */
  pendingAssignFor(appId) {
    const p = this.pendingAssign.get(appId);
    return p && !this.patient ? p : null;
  }
  /**
   * Clinician clicks "Use <app>'s patient". The visit view owns patients and
   * visits, so this only announces; the view finds-or-creates the patient,
   * assigns the visit, and calls setPatient — at which point every held
   * proposal is re-run through the normal guard (see setPatient).
   */
  assignFromApp(appId) {
    const p = this.pendingAssignFor(appId);
    if (!p) return { ok: false, code: 'NOTHING_PENDING' };
    this._assignSource = appId; // only THIS app's held context is replayed when the patient lands
    this._changed('assignPatient', { appId, name: p.name, dob: p.dob, yourId: p.yourId });
    return { ok: true };
  }

  /**
   * Apps that are not linked to the open patient right now — a proposal to pull
   * in, a mismatch to confirm, or simply no accepted context yet for this
   * patient. Badged on the tile and the Apps tab in each app's own colour.
   */
  appsNeedingAttention() {
    return this.apps.filter((a) => {
      if (!a.enabled) return false;
      if (this.pendingAssignFor(a.id) || this.pendingFor(a.id)) return true;
      return Boolean(this.patient) && !this.getBuffer(a.id);
    });
  }

  /** The rejected proposal a clinician could confirm for this app (or null). */
  pendingFor(appId) {
    const p = this.pending.get(appId);
    return p && this.patient && p.patientId === this.patient.id ? p : null;
  }

  /**
   * Clinician says "yes, same patient" in Comprehend's UI. Links the vendor's id
   * to the open chart (persisted on the patient record), accepts the context
   * that was rejected, and tells the host so the app gets a fresh `patient`
   * event with `yourId` set. Nothing in the vendor's page decides this.
   */
  confirmLink(appId) {
    const p = this.pendingFor(appId);
    if (!p) return { ok: false, code: 'NOTHING_PENDING' };
    this.pending.delete(appId);
    const result = this._acceptContext(appId, this.patient, p);
    // Persist the link even when the vendor sent no id: remember the name we accepted.
    if (p.yourId == null) this.setRef(appId, this.patient.id, `name:${p.name}`);
    this._changed('linked', { appId, patientId: this.patient.id, yourId: p.yourId, name: p.name });
    return result;
  }

  /**
   * Clinician undoes a link from Comprehend's UI: forget the vendor's id for this
   * patient (here and on the patient record), drop that app's context for the
   * visit, and tell the host so the app gets a handle without `yourId`.
   */
  unlink(appId) {
    const open = this.patient;
    if (!open) return { ok: false, code: 'NO_ENCOUNTER' };
    const key = `${appId}:${open.id}`;
    const had = this.refs[key] != null;
    delete this.refs[key];
    writeJSON(REFS_KEY, this.refs);
    if (had && this.persistRef) {
      Promise.resolve(this.persistRef(open.id, appId, null)).catch((e) => console.warn('[vendorApps] persistRef(null) failed', e));
    }
    this.pending.delete(appId);
    const hadBuffer = this.buffers.delete(key);
    this._setStatus(appId, { linkedAs: null, lastProvideAt: null, lastError: null });
    if (hadBuffer) this._changed('buffer', { appId, patientId: open.id });
    this._changed('unlinked', { appId, patientId: open.id });
    return { ok: true };
  }

  _acceptContext(appId, open, { markdown, name, yourId }) {
    const text = typeof markdown === 'string' ? markdown : '';
    const providedAt = new Date().toISOString();
    this.buffers.set(`${appId}:${open.id}`, { markdown: text, patientRef: yourId ?? null, patientName: name, providedAt });
    if (yourId != null) this.setRef(appId, open.id, yourId);
    this._setStatus(appId, { linkedAs: name, lastProvideAt: providedAt, lastError: null });
    this._changed('buffer', { appId, patientId: open.id });
    return { ok: true, truncated: false };
  }
  /** Vendor withdrew its content for a patient (link is kept). */
  clearContext(appId, patientId) {
    if (this.pendingAssign.delete(appId)) this._setStatus(appId, { awaitingPatient: null });
    const id = String(patientId ?? this.patient?.id ?? '');
    if (!id || !this.buffers.delete(`${appId}:${id}`)) return;
    this._setStatus(appId, { lastProvideAt: null });
    this._changed('buffer', { appId, patientId: id });
  }
  /** Back-compat alias for the v2 wire name. */
  provide(appId, { markdown, patientId, patientName, patientRef }) {
    return this.setContext(appId, { markdown, patientId, name: patientName, yourId: patientRef });
  }
  getBuffer(appId, patientId = this.patient?.id) {
    return patientId ? this.buffers.get(`${appId}:${patientId}`) || null : null;
  }
  /** Every populated buffer for the open patient — what the note agent reads (D1). */
  buffersForOpenPatient() {
    if (!this.patient) return [];
    return this.apps
      .map((app) => ({ app, buffer: this.getBuffer(app.id) }))
      .filter(({ buffer }) => buffer)
      .map(({ app, buffer }) => ({ appId: app.id, app: app.name, providedAt: buffer.providedAt, markdown: buffer.markdown }));
  }

  // ---- provider-level context --------------------------------------------
  /**
   * Content about the clinician using the app (their compliance to-dos,
   * credentials), not about any patient. There is no link and no name check.
   * It is saved on the clinician's user record, where chat reads it; note
   * prompts never see it. The visit's App data shows it so we can see what
   * the app is telling us.
   */
  setProviderContext(appId, { markdown }) {
    if (typeof markdown !== 'string') return { ok: false, code: 'BAD_CONTEXT', message: 'markdown must be a string' };
    if (markdown.length > MAX_CONTEXT_CHARS) {
      return { ok: false, code: 'CONTEXT_TOO_LARGE', message: `context exceeds ${MAX_CONTEXT_CHARS} characters; send a concise summary` };
    }
    if (!markdown.trim()) return this.clearProviderContext(appId);
    const current = this.providerBuffers.get(appId);
    if (current?.markdown === markdown) return { ok: true };
    this.providerBuffers.set(appId, { markdown, providedAt: new Date().toISOString() });
    this._persistProvider(appId, markdown);
    this._changed('providerBuffer', { appId });
    return { ok: true };
  }
  clearProviderContext(appId) {
    if (this.providerBuffers.delete(appId)) {
      this._persistProvider(appId, '');
      this._changed('providerBuffer', { appId });
    }
    return { ok: true };
  }
  _persistProvider(appId, markdown) {
    if (!this.persistProviderContext || !this.getApp(appId)) return;
    Promise.resolve(this.persistProviderContext(appId, markdown)).catch((e) => console.warn('[vendorApps] persistProviderContext failed', e));
  }
  /** What the user record already holds (from an earlier session). Never overrides this session's copy. */
  hydrateProviderContext(saved) {
    if (!saved || typeof saved !== 'object') return;
    Object.entries(saved).forEach(([appId, entry]) => {
      if (this.providerBuffers.has(appId) || typeof entry?.markdown !== 'string' || !entry.markdown.trim()) return;
      this.providerBuffers.set(appId, { markdown: entry.markdown, providedAt: entry.providedAt || null });
      this._changed('providerBuffer', { appId });
    });
  }
  /** Every enabled app's provider-level content, for the visit's App data. */
  providerBuffersForVisit() {
    return this.apps
      .filter((app) => app.enabled && this.providerBuffers.has(app.id))
      .map((app) => ({ appId: app.id, app: app.name, ...this.providerBuffers.get(app.id) }));
  }

  // ---- subscriptions -----------------------------------------------------
  subscribe(appId, subId, structure) {
    const forApp = this.subs.get(appId) || new Map();
    if (!forApp.has(subId) && forApp.size >= MAX_SUBSCRIPTIONS) {
      return { ok: false, code: 'TOO_MANY_SUBSCRIPTIONS', message: `max ${MAX_SUBSCRIPTIONS} live subscriptions per app` };
    }
    let compiled;
    try {
      compiled = compileStructure(structure);
    } catch (e) {
      return { ok: false, code: 'BAD_STRUCTURE', reason: 'MALFORMED', message: e.message };
    }
    forApp.set(subId, { structure, compiled: compiled.structure, types: compiled.types, summary: compiled.summary === true, hash: hashStructure(structure || {}), lastKey: null });
    this.subs.set(appId, forApp);
    this._changed('subs', { appId });
    return { ok: true };
  }
  unsubscribe(appId, subId) {
    const forApp = this.subs.get(appId);
    if (forApp?.delete(subId)) this._changed('subs', { appId });
  }
  subsFor(appId) {
    return this.subs.get(appId) || new Map();
  }
  clearApp(appId) {
    this.subs.delete(appId);
    this.status.delete(appId);
    this._changed('subs', { appId });
  }

  // ---- status (what the rail shows) --------------------------------------
  _setStatus(appId, patch) {
    this.status.set(appId, { ...(this.status.get(appId) || {}), ...patch });
    this._changed('status', { appId });
  }
  statusFor(appId) {
    return this.status.get(appId) || {};
  }
}

/**
 * The packaged TPI connector, whatever id its registration got (va_…): its
 * launch page is our hosted /vendor-apps/tpi/ wrapper. Fixtures use id 'tpi'.
 */
export const isTpiApp = (app) => {
  if (!app) return false;
  if (app.id === 'tpi') return true;
  try {
    return new URL(app.launchUrl).pathname.replace(/index\.html$/, '') === '/vendor-apps/tpi/';
  } catch (e) {
    return false;
  }
};

export const vendorStore = new VendorStore();

/**
 * True when this code is running inside the Chrome extension (side panel /
 * popup). Vendor Apps runs there too — panel-based, never injected into the
 * EMR tab. A remote page in an <iframe> executes in its own origin, not the
 * extension's, so it is not "remotely hosted code" under MV3; what the
 * extension side needs is (1) vendors allowing chrome-extension://<our id> in
 * frame-ancestors, (2) comprehend.js accepting that origin as a host, and
 * (3) the store privacy disclosure naming the third-party sharing.
 */
export const isExtensionContext = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (window.location?.protocol === 'chrome-extension:') return true;
    return Boolean(window.chrome?.runtime?.id);
  } catch (e) {
    return false;
  }
};

/** Feature gate used by the Apps tab, Admin → Apps and the host: backend flag or the Dev Console's local override. Same on web and in the extension panel. */
export const isVendorAppsEnabled = (apiClient, store = vendorStore) =>
  Boolean(apiClient?.features?.vendor_apps) || store.devEnabled;

export default vendorStore;
